import { formatCurrency } from '@/utils/currencyUtils';
import { MonetaryAmount } from '@/types';

import subcategories from '@/data/campaignTypes.json';

interface TooltipPayload {
  name: string;
  total: MonetaryAmount;
  impressions: number;
  percentage: number;
}

interface CampaignTypesTooltipContentProps {
  payload: TooltipPayload | null;
}

/**
 * Content component for chart tooltips displaying value and currency information
 */
export const CampaignTypesTooltipContent = ({ payload }: CampaignTypesTooltipContentProps) => {
  if (!payload) return null;

  const { name, total } = payload;

  const description =
    (subcategories as Record<string, Array<string>>)[name.toLowerCase().replace(/\s+/g, '_')] || '';

  /**
   * Return the percentage value with no decimals if it rounds to 1 or greater, or with 1 decimal place if less than 1
   *
   * @param value - The raw value.
   * @returns the correctly rounded value.
   */
  const formatValue = (value: number): number => {
    if (!value) {
      return 0;
    }

    if (value < 1) {
      return parseFloat(value.toFixed(1));
    }

    return Math.round(value);
  };

  return (
    <div className="flex flex-col gap-2 p-2 w-[200]px">
      <p className="text-sm font-medium capitalize mr-6">{name}</p>
      <div>
        <p className="font-medium text-[#4040A8]">
          {formatCurrency(total?.amount || 0, total?.currency)}
        </p>
        <p className="text-sm text-textMedGrey">estimated earnings</p>
      </div>
      <div>
        <p className="font-medium text-[#4040A8]">{formatValue(payload.percentage)}%</p>
        <p className="text-sm text-textMedGrey">of total</p>
      </div>
      {/* Filtering out ads and programmatic as they do not have subcategories and their descriptions are used in the campaign types modal table. */}
      {name.toLowerCase() !== 'ads' &&
        name.toLowerCase() !== 'programmatic' &&
        description.length > 0 && (
          <div className="text-xs text-textMedGrey">
            <span>Can include:</span>
            <ul className="list-disc pl-4">
              {description.map((subcat) => (
                <li key={subcat}>{subcat}</li>
              ))}
            </ul>
          </div>
        )}
    </div>
  );
};
